import { useNavigate } from 'react-router-dom';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import Button from '../common/button.tsx';
import Modal from '../common/modal.tsx';
import QuizAnswers from './quiz-answers.tsx';
import { useState } from 'react';
import QuizQuestionsReview from './quiz-questions-review.tsx';
import { cn, orderToLetter } from '../../utils/common-utils.ts';
import { skipToken, useSuspenseQuery } from '@apollo/client';
import {
  GET_QUIZ_ATTEMPT_REVIEW,
  QUIZ_ATTEMPT_FRAGMENT,
} from '../../apollo/quiz-attempts.ts';
import { useFragment } from '../../apollo/__generated__';
import { convertQuizScore } from './quiz-attempt-utils.ts';
import {
  useCreateQuizAttempt,
} from './quiz-attempt-hooks.ts';
import { QuizAttemptStatus } from '../../apollo/__generated__/graphql.ts';

interface IProps {
  attemptId: number;
  isReviewMode?: boolean;
}

const AttemptFinish = ({ attemptId, isReviewMode = false }: IProps) => {
  const navigate = useNavigate();
  const { startNewQuiz, isLoading: isRetaking } = useCreateQuizAttempt();
  const [isQuizReview, setIsQuizReview] = useState(false);
  const [isQuestionsReview, setIsQuestionsReview] = useState(false);

  const { data } = useSuspenseQuery(
    GET_QUIZ_ATTEMPT_REVIEW,
    attemptId
      ? {
          variables: { input: { id: attemptId } },
          errorPolicy: 'all',
        }
      : skipToken,
  );
  const attempt = useFragment(
    QUIZ_ATTEMPT_FRAGMENT,
    data?.quizAttempt?.getAttempt,
  );
  const quiz = data?.quizAttempt?.getAttempt.quiz;
  const answers = quiz?.quizQuestions?.map((item) => {
    const question = attempt?.questions.find(
      (attemptQuestion) => attemptQuestion.questionId === item.question?.id,
    );
    const isChoiceType = question?.question?.answerType === 'MultipleChoice';
    const correctAnswer = question?.correctChoice
      ? isChoiceType
        ? orderToLetter(question.correctChoice.order)
        : question.correctChoice.text || ''
      : '';
    const userAnswer = isChoiceType
      ? question?.choice
        ? orderToLetter(question.choice.order)
        : ''
      : question?.text || '';

    return {
      order: item.order,
      correctAnswer,
      userAnswer,
      isCorrect: !!question?.correct,
    };
  });
  const isSkippedAttempt = attempt?.status === QuizAttemptStatus.Skipped;

  if (!attempt)
    return <p className={'mt-20 text-center text-dark-blue'}>Error</p>;
  return (
    <div className={'flex-center'}>
      <section
        className={cn(
          'w-2xl p-8',
          isReviewMode ? 'mt-0' : 'mt-40 rounded-xl border border-table-border',
        )}
      >
        <div
          className={'flex flex-col items-center justify-center text-center'}
        >
          {!isReviewMode && (
            <div className="flex flex-col justify-center">
              <h1 className="text-gray">{quiz?.name}</h1>
              <h1>{isSkippedAttempt ? 'Skipped' : "You're All Finished!"}</h1>
              {!isSkippedAttempt && (
                <CheckBadgeIcon className="my-5 h-24 w-24 text-green" />
              )}
            </div>
          )}

          <div>
            {isSkippedAttempt ? (
              <p className={'mx-auto max-w-md text-base'}>
                This quiz attempt was skipped.
              </p>
            ) : (
              <>
                <p className={'text-xl font-medium'}>
                  Score: {convertQuizScore(attempt.score ?? undefined)}
                </p>

                <div
                  className={
                    'mx-auto my-2 block h-3 w-64 rounded-lg bg-light-blue'
                  }
                >
                  <div
                    className={'h-3 rounded-lg bg-green'}
                    style={{ width: `${(attempt.score ?? 0) * 100}%` }}
                  />
                </div>

                {!isReviewMode && (
                  <p className={'my-4'}>
                    Congratulations on completing your quiz
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className={'mt-6 grid w-[350px] grid-cols-1 gap-3'}>
          {!isReviewMode && !isSkippedAttempt && (
            <Button onClick={() => navigate('/')} white>
              Return home
            </Button>
          )}
          {isSkippedAttempt ? (
            <Button
              onClick={() => startNewQuiz(quiz?.id)}
              isLoading={isRetaking}
            >
              Retake
            </Button>
          ) : (
            <>
              <Button onClick={() => setIsQuizReview(true)}>View Answers</Button>
              <Button onClick={() => setIsQuestionsReview(true)}>Review</Button>
            </>
          )}
        </div>
      </section>

      <Modal
        aboveTitle={quiz?.name}
        title={'Quiz Review'}
        onClose={() => setIsQuizReview(false)}
        isOpen={isQuizReview}
      >
        <QuizAnswers data={answers} />
      </Modal>

      <Modal
        aboveTitle={quiz?.name}
        title={'Questions Review'}
        onClose={() => setIsQuestionsReview(false)}
        isOpen={isQuestionsReview}
      >
        <QuizQuestionsReview quizAttemptId={attempt.id} />
      </Modal>
    </div>
  );
};
export default AttemptFinish;
